<template>
  <div>
    <div v-if="showConfetti" class="confetti-overlay">
      <Vue3Lottie
        :animationData="confettiAnimation"
        :autoPlay="true"
        :loop="false"
        @onComplete="onConfettiComplete"
        ref="confettiRef"
      />
    </div>

    <div @click="viewDetails()" class="marketplace-product-card">
    <div class="product-image-container">
      <img :src="(product.images && product.images.length > 0) ? product.images[0] : defaultImage" :alt="product.title" class="product-image" />
      <div class="promotion-badge">
        <span>{{ product.marketplaceModule.moduleName}}</span>
      </div>
      <div v-if="product.isAccepted === null" class="status-badge">
        <span>{{ $t("waiting_for_approval") }}</span>
      </div>
    </div>

    <div class="product-content">
    <div class="product-info-one">
      <span class="product-category-span">{{product.marketplaceCategory.title}}</span>
      <div class="product-info-two">
        <div class="stars-row">
          <span class="rating-text">{{ product.commentStats.averageRating }}</span>
          <img v-for="star in fullStars" :key="'full-' + star" src="@/assets/images/starFill.svg" class="star-icon" />
          <img v-if="hasHalfStar" src="@/assets/images/starFill.svg" class="star-icon half-star" />
          <img v-for="star in emptyStars" :key="'empty-' + star" src="@/assets/images/starOutline.svg" class="star-icon" />
        </div>
        <div class="product-date">
          <img src="../assets/images/calendar.svg" />
          <span class="product-category-span">{{ $t('due_date') }}: </span>
          <span class="product-category-span">{{ formatDate(product.endDate) }}</span>
        </div>
      </div>
    </div>

      <div class="product-info-view">
        <span class="product-info-title">
          {{product.title}}

        </span>
        <span class="product-info-content">
          {{truncateDescription(product.description)}}
        </span>
        <div>
        </div>
      </div>
      </div>
    <div class="product-info-company" >
      <img v-if="product.companyInfo.logo" class="company-info-avatar" :src="product.companyInfo.logo">
      <div v-else class="company-info-avatar-placeholder">
        <span>{{ getCompanyInitials() }}</span>
      </div>
      <span class="company-info-title">{{product.companyInfo.name}}</span>
    </div>
    <div class="primary-button" @click.stop="handlePriceButtonClick" :class="{ 'my-product': product.isMyProduct === true, 'purchased': product.isPurchased === true }">
      <span v-if="product.isMyProduct === true" class="price-view">{{ $t('your_product') }}</span>
      <span v-else-if="product.isPurchased === true" class="price-view">{{$t('purchased')}}</span>
      <span v-else-if="product.price === '0.00'" class="price-view">{{$t('free')}}</span>
      <span v-else class="price-view">{{product.price}} {{product.currency}}</span>
    </div>
  </div>

    <PurchaseModal
      :isVisible="showPurchaseModal"
      :product="getPurchaseProductData()"
      @close="closePurchaseModal"
      @payment-success="onPaymentSuccess"
    />
  </div>
</template>

<script>
import {formatDate} from "@fullcalendar/core";
import defaultImage from '@/assets/images/ibility-default.jpeg';
import PurchaseModal from '@/components/modals/PurchaseModal.vue';
import { Vue3Lottie } from 'vue3-lottie';
import confettiData from '@/assets/animations/confetti.json';

export default {
  name: 'MarketplaceProductCard',
  components: {
    PurchaseModal,
    Vue3Lottie
  },
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      defaultImage,
      showPurchaseModal: false,
      showConfetti: false,
      confettiAnimation: confettiData
    }
  },

  computed: {
    isMyProduct() {
      return this.product?.isMyProduct === true;
    },
    fullStars() {
      const rating = Number(this.product.commentStats.averageRating) || 0;
      return Math.floor(rating);
    },
    hasHalfStar() {
      const rating = Number(this.product.commentStats.averageRating) || 0;
      return rating % 1 >= 0.5;
    },
    emptyStars() {
      const rating = Number(this.product.commentStats.averageRating) || 0;
      const filled = Math.floor(rating) + (rating % 1 >= 0.5 ? 1 : 0);
      return Math.max(0, 5 - filled);
    }
  },

  methods: {
    formatDate,

    truncateDescription(description) {
      if (!description) return '';
      return description.length > 120 ? description.substring(0, 120) + '...' : description;
    },


    viewDetails() {
      this.$router.push(`/marketplace/product/${this.product.uuid}`);
    },

    getCompanyInitials() {
      if (this.product.companyInfo?.name) {
        return this.product.companyInfo.name.substring(0, 2).toUpperCase();
      }
      return 'CO';
    },

    handlePriceButtonClick() {
      if (this.product?.isMyProduct === true || this.product?.isPurchased === true) {
        return;
      }

      this.openPurchaseModal();
    },

    openPurchaseModal() {
      this.showPurchaseModal = true;
    },

    closePurchaseModal() {
      this.showPurchaseModal = false;
    },

    onPaymentSuccess(paymentData) {
      this.showPurchaseModal = false;

      this.showConfetti = true;

      this.$snackbar.add({
        type: 'success',
        text: this.$t('payment_successful')
      });

      this.$emit('payment-success', paymentData);
    },

    onConfettiComplete() {
      this.showConfetti = false;
    },

    getPurchaseProductData() {
      return {
        uuid: this.product.uuid,
        title: this.product.title,
        price: this.product.price,
        formattedPrice: this.product.formattedPrice || `${this.product.price} ${this.product.currency}`,
        exchangeRate: this.product.exchangeRate || null,
        image: (this.product.images && this.product.images.length > 0) ? this.product.images[0] : this.defaultImage,
        companyName: this.product.companyInfo?.name || `Company ${this.product.companyId}`
      };
    },


  },
}
</script>

<style scoped>
.marketplace-product-card {
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'euclid_medium', sans-serif;
}
.product-image-container {
  position: relative;
  min-height: 200px;
  overflow: hidden;
  width: 100%;
}

.product-image {
  width: 100%;
  max-height: 170px;
  object-fit: cover;
  border-radius: 10px;
}

.promotion-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #ff4757;
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge {
  position: absolute;
  top: 12px;
  background: #ff4757;
  color: white;
  right: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.active {
  background: #2ed573;
  color: white;
}

.status-badge.inactive {
  background: #747d8c;
  color: white;
}

.product-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.stars-row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.star-icon {
  width: 14px;
  height: 14px;
}

.half-star {
  opacity: 0.5;
}

.rating-text {
  font-family: 'euclid_bold', sans-serif;
  font-size: 14px;
  color: #101828;
  margin-left: 4px;
}


.product-category-span{
  font-family: 'euclid_circular_a Std', sans-serif;
  font-size: 12px;
  color: #868C98;
  line-height: 20px;
  font-weight: 400;
}

.product-info-one{
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
}
.product-info-two{
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;

}
.primary-button{
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  background: #101828;
  border-radius: 12px;
  width: 100%;
  height: 40px;
  cursor: pointer;
}

.primary-button.purchased {
  background: #EAECF0;
  cursor: default;
}

.primary-button.my-product {
  background: #6366f1;
  cursor: default;
}

.primary-button.purchased {
  background: #10b981;
  cursor: default;
}
.price-view {
  font-family: 'euclid_medium', sans-serif;
  font-size: 16px;
  color: #ffffff;
  line-height: 24px;
  font-weight: 500;
  padding: 8px 16px;
}

.product-info-view{
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
}
.product-info-title{
  font-family: 'euclid_medium', sans-serif;
  font-size: 16px;
  color: #101828;
  line-height: 24px;
  font-weight: 500;
}
.product-info-content{
  font-family: 'euclid_medium Std', sans-serif;
  font-size: 12px;
  color: #667085;
  line-height: 20px;
  font-weight: 400;
}
.product-info-company{
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.company-info-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.company-info-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}
.company-info-title {
  font-family: 'euclid_medium', sans-serif;
  font-size: 14px;
  color: #101828;
  line-height: 20px;
  font-weight: 500;
}

/* Full Screen Confetti */
.confetti-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  background: rgba(0, 0, 0, 0.1) !important;
  pointer-events: none !important;
}

.confetti-overlay .lottie-container,
.confetti-overlay > div {
  max-width: 100vw;
  max-height: 100vh;
  width: 100%;
  height: 100%;
}
</style>
